import {
  type PortalDashboardStats,
  type PortalEmployeeList,
  type PortalEmployeeListParams,
  type PortalImportHistoryItem,
  type PortalImportResponse,
  getPortalDashboard,
  getPortalEmployees,
  getPortalImportHistory,
  importPortalEmployees,
  invitePortalEmployee,
} from '@/services/portal.service'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export const portalKeys = {
  all: ['portal'] as const,
  dashboard: () => [...portalKeys.all, 'dashboard'] as const,
  employees: () => [...portalKeys.all, 'employees'] as const,
  employeeList: (params: PortalEmployeeListParams) =>
    [...portalKeys.employees(), 'list', params] as const,
  imports: () => [...portalKeys.all, 'imports'] as const,
}

export function usePortalDashboard() {
  return useQuery<PortalDashboardStats>({
    queryKey: portalKeys.dashboard(),
    queryFn: () => getPortalDashboard(),
  })
}

export function usePortalEmployees(params: PortalEmployeeListParams = {}) {
  return useQuery<PortalEmployeeList>({
    queryKey: portalKeys.employeeList(params),
    queryFn: () => getPortalEmployees(params),
  })
}

export function usePortalImport() {
  const queryClient = useQueryClient()

  return useMutation<PortalImportResponse, Error, File>({
    mutationFn: (file: File) => importPortalEmployees(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.employees() })
      queryClient.invalidateQueries({ queryKey: portalKeys.dashboard() })
      queryClient.invalidateQueries({ queryKey: portalKeys.imports() })
    },
  })
}

export function useInviteEmployee() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (employeeId: number) => invitePortalEmployee(employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.employees() })
      queryClient.invalidateQueries({ queryKey: portalKeys.dashboard() })
    },
  })
}

export function usePortalImportHistory() {
  return useQuery<PortalImportHistoryItem[]>({
    queryKey: portalKeys.imports(),
    queryFn: () => getPortalImportHistory(),
  })
}
